import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { useFilters } from "@/lib/filters";
import { fmtCurrency, fmtNumber, fmtRelative } from "@/lib/format";
import { num, rows } from "@/lib/kpi";
import {
  BarList,
  ErrorState,
  GapList,
  KpiCard,
  LoadingState,
  PageHeader,
  Panel,
  StatusPill,
} from "@/components/dash/ui";

type CompanyStock = { name: string; units: number; value: number };
type LocationStock = { name: string; units: number; value: number };

export default function StockPage() {
  const { range } = useFilters();
  const q = useQuery({ queryKey: ["src", "odoo_stock", range], queryFn: () => dashApi.source("odoo_stock", range) });
  const k = q.data?.kpis;
  const value = num(k, "stock_value");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Stock / Inventory"
        description="On-hand inventory from Odoo only. Quantities are current on-hand in the API user's company context (not a period metric)."
        right={q.data && <span className="flex items-center gap-2"><StatusPill status={q.data.status} /><span className="text-xs text-slate-500">{fmtRelative(q.data.captured_at)}</span></span>}
      />
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-4">
            <KpiCard label="Storable products" value={num(k, "total_products")} unit="count" source="odoo_stock" />
            <KpiCard label="In stock" value={num(k, "in_stock")} unit="count" />
            <KpiCard label="Zero / negative" value={num(k, "zero_stock")} unit="count" />
            <KpiCard label="Low stock (≤ threshold)" value={num(k, "low_stock")} unit="count" tone="danger" />
            <KpiCard label="On-hand units" value={num(k, "stock_units")} unit="count" />
            <KpiCard label="Stock value" value={value} unit="EUR" />
            <KpiCard label="Warehouses" value={num(k, "warehouses")} unit="count" />
            <KpiCard label="Internal locations" value={num(k, "internal_locations")} unit="count" />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Panel title="On-hand by company" subtitle="Internal locations only">
              <CompanyTable rows={rows<CompanyStock>(k, "by_company")} showValue={value !== null} />
            </Panel>
            <Panel title="Top products by units on hand">
              <BarList rows={rows<{ name: string; value: number }>(k, "top_products").map((r) => ({ label: r.name, value: r.value }))} unit="count" empty="No on-hand stock." />
            </Panel>
          </div>

          <Panel title="On-hand by location / warehouse">
            <LocationTable rows={rows<LocationStock>(k, "by_location")} showValue={value !== null} />
          </Panel>

          {q.data && <GapList gaps={q.data.gaps} />}
        </>
      )}
    </div>
  );
}

function CompanyTable({ rows, showValue }: { rows: CompanyStock[]; showValue: boolean }) {
  if (!rows.length) return <p className="py-4 text-center text-sm text-slate-500">No stock by company.</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
          <th className="pb-2 font-medium">Company</th>
          <th className="pb-2 text-right font-medium">Units</th>
          {showValue && <th className="pb-2 text-right font-medium">Value</th>}
        </tr>
      </thead>
      <tbody className="divide-y divide-ink-500/40">
        {rows.map((r, i) => (
          <tr key={i} className="text-slate-300">
            <td className="py-2">{r.name}</td>
            <td className="py-2 text-right tabular-nums text-white">{fmtNumber(r.units)}</td>
            {showValue && <td className="py-2 text-right tabular-nums text-slate-400">{fmtCurrency(r.value)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LocationTable({ rows, showValue }: { rows: LocationStock[]; showValue: boolean }) {
  if (!rows.length) return <p className="py-4 text-center text-sm text-slate-500">No stock by location.</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
          <th className="pb-2 font-medium">Location</th>
          <th className="pb-2 text-right font-medium">Units</th>
          {showValue && <th className="pb-2 text-right font-medium">Value</th>}
        </tr>
      </thead>
      <tbody className="divide-y divide-ink-500/40">
        {rows.map((r, i) => (
          <tr key={i} className="text-slate-300">
            <td className="py-2">{r.name}</td>
            <td className="py-2 text-right tabular-nums text-white">{fmtNumber(r.units)}</td>
            {showValue && <td className="py-2 text-right tabular-nums text-slate-400">{fmtCurrency(r.value)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
